import React, { useEffect, useRef, useState } from 'react';
import SkeletonImage from '../Skeleton/skeleton-image-component';
import useScreenSize from '../../Hooks/useScreenSize';
import useVideoInView from '../../Hooks/useVideoInView';
import { toMediaUrl } from '../../Services/ApiService';

function SkeletonLayer({ visible }) {
    return (
        <div className={`absolute inset-0 z-10 transition-opacity duration-500 ease-in-out ${visible ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
            <SkeletonImage />
        </div>
    );
}

function HoverOverlay({ visible, children }) {
    return (
        <div className={`absolute top-0 left-0 w-full h-full transition-opacity duration-700 z-20 ${visible ? 'opacity-100' : 'opacity-0'}`}>
            <div className="absolute top-0 left-0 w-full h-full dark:bg-gray-900 bg-white opacity-0 hover:opacity-60 rounded-lg transition-all ease-in-out duration-300 flex justify-center items-center">
                <div className="font-bold text-xl text-black">
                    {children}
                </div>
            </div>
        </div>
    );
}

/**
 * Autoplaying, lazy inline video.
 *
 * - Sources are served through the backend's media route (toMediaUrl), which
 *   upgrades raw uploads to their transcoded -web.mp4 version.
 * - The <video> mounts only near the viewport and pauses when scrolled away
 *   (useVideoInView), so media-heavy pages stay light.
 * - `src` sits directly on <video> with no <source type>: a declared type like
 *   video/quicktime makes Chrome skip the source without fetching, but our
 *   /media/*.mov URLs actually serve a playable mp4.
 */
const VideoComponent = ({
    video: videoProp,
    autoPlayWidth = 0,
    children,
    className = '',
    wrapperClassName = '',
    useSekeleton = true,
}) => {
    const video = toMediaUrl(videoProp);
    const wrapperRef = useRef(null);
    const videoRef = useRef(null);
    const [loaded, setLoaded] = useState(false);

    const { width } = useScreenSize();
    const shouldMount = useVideoInView(wrapperRef, videoRef, video);

    useEffect(() => {
        setLoaded(false);
    }, [video]);

    if (width && width <= autoPlayWidth) return null;

    return (
        <div ref={wrapperRef} className={`relative overflow-hidden w-full h-full ${wrapperClassName}`.trim()}>
            <SkeletonLayer visible={useSekeleton && !loaded} />

            {shouldMount && (
                <video
                    key={video} // forces re-mount when the URL changes
                    ref={videoRef}
                    src={video}
                    autoPlay
                    loop
                    muted
                    playsInline
                    preload="metadata"
                    onLoadedData={() => setLoaded(true)}
                    onError={() => setLoaded(true)} // drop the skeleton so failures aren't invisible
                    className={`w-full h-full object-cover transition-opacity duration-700 ease-in-out ${loaded ? 'opacity-100' : 'opacity-0'} ${className}`.trim()}
                />
            )}

            {children && <HoverOverlay visible={loaded}>{children}</HoverOverlay>}
        </div>
    );
};

export default VideoComponent;
